import React, { useState } from 'react';
import { adjustStamps } from '../../../services/api';
import { X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const REASONS = [
  { value: 'error_correction', label: 'Corrección de error' },
  { value: 'courtesy', label: 'Cortesía' },
  { value: 'migration', label: 'Migración' },
];

const StampAdjustModal = ({ serial, onClose, onSuccess }) => {
  const [form, setForm] = useState({ quantity: '', description: '', reason: 'error_correction' });
  const [submitting, setSubmitting] = useState(false);

  const qty = parseInt(form.quantity) || 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (qty === 0) return toast.error('La cantidad no puede ser 0');
    if (!form.description.trim()) return toast.error('La descripción es requerida');

    setSubmitting(true);
    try {
      const res = await adjustStamps({
        serial_number: serial,
        quantity: qty,
        description: form.description,
        reason: form.reason,
      });
      toast.success(`Ajuste aplicado. Nuevo saldo: ${res.data.new_balance}`);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al aplicar ajuste');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-xl font-bold text-slate-900">Ajuste de Timbres</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-slate-50 p-3 rounded-lg">
            <p className="text-xs text-slate-500">Licencia</p>
            <code className="text-sm font-mono font-bold text-slate-900">{serial}</code>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Cantidad *</label>
            <input
              type="number"
              required
              value={form.quantity}
              onChange={(e) => setForm({ ...form, quantity: e.target.value })}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              placeholder="Ej: 10 (positivo suma, negativo resta)"
            />
            {qty !== 0 && (
              <p className={`text-xs mt-1 font-medium ${qty > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {qty > 0 ? `+${qty} timbres se agregarán` : `${qty} timbres se restarán`}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Razón *</label>
            <select
              required
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              {REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Descripción *</label>
            <textarea
              required
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              placeholder="Explica el motivo del ajuste..."
            />
          </div>

          {qty < 0 && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-700">Esto reducirá el saldo de timbres de esta licencia.</p>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium transition-colors disabled:opacity-50"
            >
              {submitting ? 'Aplicando...' : 'Aplicar Ajuste'}
            </button>
            <button type="button" onClick={onClose} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 font-medium transition-colors">
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StampAdjustModal;
